import { useEffect, useState } from 'react';
import { api } from '@/lib/api-client';
import { usePermissions } from '@/hooks/use-permissions';
import { Permissions } from '@/lib/permissions';
import { PermissionGate } from '@/components/auth/PermissionGate';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, RefreshCw, Play, CheckCircle } from 'lucide-react';

interface SettlementRun {
  id: string;
  period_start: string;
  period_end: string;
  status: string;
  total_amount: number;
  currency: string;
  created: string;
}

interface DLQEntry {
  id: string;
  event_type: string;
  error?: string;
  status: string;
  created: string;
}

export default function PlatformBillingPage() {
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(Permissions.PLATFORM_BILLING_WRITE);
  const [runs, setRuns] = useState<SettlementRun[]>([]);
  const [margin, setMargin] = useState(0);
  const [dlq, setDlq] = useState<DLQEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState(false);
  const [resolving, setResolving] = useState<string | null>(null);

  const fetchAll = async () => {
    try {
      const [runsRes, marginRes, dlqRes] = await Promise.all([
        api.get('/billing/settlement/runs'),
        api.get('/billing/ledger/margin'),
        api.get('/billing/dlq').catch(() => ({ data: { entries: [] } })),
      ]);
      setRuns(runsRes.data.runs || runsRes.data || []);
      setMargin(marginRes.data.total_cents || 0);
      setDlq(dlqRes.data.entries || []);
    } catch (err) {
      console.error('PlatformBillingPage:fetchAll - Failed to fetch billing data', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const triggerSettlement = async () => {
    setTriggering(true);
    try {
      await api.post('/billing/settlement/run');
      fetchAll();
    } catch (err) {
      console.error('PlatformBillingPage:triggerSettlement - Settlement trigger failed', err);
    } finally {
      setTriggering(false);
    }
  };

  const resolveDLQ = async (id: string) => {
    setResolving(id);
    try {
      await api.put(`/billing/dlq/${id}/resolve`);
      fetchAll();
    } catch (err) {
      console.error('PlatformBillingPage:resolveDLQ - Resolve DLQ failed', err);
    } finally {
      setResolving(null);
    }
  };

  const formatCents = (c: number) => `$${(c / 100).toFixed(2)}`;
  const statusColor = (s: string) => {
    if (s === 'completed') return 'bg-emerald-100 text-emerald-800';
    if (s === 'partial') return 'bg-amber-100 text-amber-800';
    if (s === 'failed') return 'bg-red-100 text-red-800';
    return 'bg-slate-100 text-slate-800';
  };

  if (loading) return <div className="p-6 text-muted-foreground">Loading platform billing...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Platform Billing</h1>
          <p className="text-muted-foreground">Settlement runs, margins, and system health</p>
        </div>
        <PermissionGate require={[Permissions.PLATFORM_BILLING_WRITE]} hide>
          <Button onClick={triggerSettlement} disabled={triggering}>
            <Play className="w-4 h-4 mr-2" />{triggering ? 'Running...' : 'Run Settlement'}
          </Button>
        </PermissionGate>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Platform Margin (Period)</p>
          <p className="text-2xl font-semibold text-emerald-600">{formatCents(margin)}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Settlement Runs</p>
          <p className="text-2xl font-semibold">{runs.length}</p>
        </CardContent></Card>
        <Card><CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">DLQ Entries</p>
          <p className="text-2xl font-semibold">{dlq.filter(d => d.status !== 'resolved').length}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Settlement Runs</CardTitle></CardHeader>
        <CardContent className="p-0">
          {runs.length === 0 ? (
            <p className="p-6 text-center text-muted-foreground">No settlement runs</p>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Period</TableHead><TableHead>Status</TableHead><TableHead>Total</TableHead><TableHead>Date</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{new Date(r.period_start).toLocaleDateString()} — {new Date(r.period_end).toLocaleDateString()}</TableCell>
                    <TableCell><Badge className={statusColor(r.status)}>{r.status}</Badge></TableCell>
                    <TableCell>{formatCents(r.total_amount)}</TableCell>
                    <TableCell className="text-muted-foreground">{new Date(r.created).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {(canWrite && (r.status === 'failed' || r.status === 'partial')) && (
                        <Button size="sm" variant="outline" onClick={() => api.post(`/billing/settlement/runs/${r.id}/retry`).then(fetchAll)}>
                          <RefreshCw className="w-4 h-4 mr-1" />Retry
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {dlq.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Dead Letter Queue</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Event Type</TableHead><TableHead>Error</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead><TableHead>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {dlq.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono text-sm">{d.event_type}</TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{d.error || '—'}</TableCell>
                    <TableCell><Badge variant="outline">{d.status}</Badge></TableCell>
                    <TableCell className="text-muted-foreground">{new Date(d.created).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {canWrite && d.status !== 'resolved' && (
                        <Button size="sm" variant="outline" onClick={() => resolveDLQ(d.id)} disabled={resolving === d.id}>
                          <CheckCircle className="w-4 h-4 mr-1" />{resolving === d.id ? '...' : 'Resolve'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
